/**
 * @module root/web
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
import '@angular/compiler';
import { AemModule } from '@core/aem';
import { NgModule, ApplicationRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, RouteReuseStrategy } from '@angular/router';
import { CoreModule } from '@core/core.module';
import { AppStateService } from '@core/root';
import { LandingWebModule } from '@experience/landing/web/landing-web.module';
import { PermissionOverrideModalService } from '@support/shared/services/permission-override-modal.service';
import { RootModule, APP_PROVIDERS } from '../root.module';
import { WebComponent } from './web.component';
import { ENV_PROVIDERS } from '@environment/decorators';
import { WEB_ROUTES } from './web.routes';
import { RootSharedModule } from '../shared/shared.module';
import { IngenicoService } from '@support/ingenico';
import { VoidIngenicoService } from '@support/ingenico/services/void-ingenico.service';
import { SharedModule } from '@support/shared/shared.module';
import { CustomerProfileComponent } from './../../support/customer-login/components/customer-profile.component';
import { WorkerIpcService } from '@core/worker/services/worker-ipc.service';
import { WidgetsModule } from '@support/shared/widgets/widgets.module';
import { CartSupportModule } from '@support/cart-support/cart-support.module';
import { StateObjectHashUtilitiesModule } from '@support/state-object-hash-utilities/state-object-hash-utilities.module';
import { ShopModule } from '@experience/shop/shop.module';
import { VestaModule } from '@support/vesta/vesta.module';
import { SharedExperienceModule } from '@experience/shared-experience/shared-experience.module';
import { NgRadio } from '@experience/reb-pdl/components/ng-radio.component';
import { PdlModule } from '@experience/reb-pdl/pdl.module';
import { CustomReuseStrategy } from '@support/reuse-route/reuse-route';
import { ProductSelectorModule } from '@experience/shop/product-selector/product-selector.module';
import { SharedCartModule } from '@experience/shop/cart/shared-cart-modules/shared-cart.module';
import { InfineaService } from '@support/infinea/services/infinea.service';
import { VoidInfineaService } from '@support/infinea/services/void-infinea.service';
import { UIPermissionService } from '@support/shared/widgets/services/ui-permission.service';
import { GlobalErrorService } from '@support/shared/widgets/services/global-error.service';
import { AppCustomPreloader } from '@root/custom-loader';
import { HttpClientJsonpModule, HttpClientModule } from '@angular/common/http';
import { RepdashStoreUtilsService } from '@support/repdash-store-utilities/utils/repdash-store-utils.service';
import { VoidRepdashStoreUtilsService } from '@support/repdash-store-utilities/utils/void-repdash-store-utils.service';
import { ReceiptRetrievalFeatureRouteGuard } from '@core/guards/receipt-retrieval-feature-route.guard';
import { PdlWrapperService } from '@core/services/pdl-wrapper.service';
/**
 * Entry Module used for serving up the Web application
 * @access private
 */
@NgModule({
  declarations: [WebComponent, CustomerProfileComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    SharedModule,
    LandingWebModule,
    FormsModule,
    RootSharedModule,
    SharedCartModule,
    WidgetsModule,
    HttpClientModule,
    HttpClientJsonpModule,
    AemModule,
    StateObjectHashUtilitiesModule,
    VestaModule,
    ProductSelectorModule,
    ShopModule,
    SharedExperienceModule,
    CartSupportModule,
    PdlModule,
    RouterModule.forRoot(WEB_ROUTES, {
      preloadingStrategy: AppCustomPreloader,
      scrollPositionRestoration: 'enabled',
    }),
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    { provide: InfineaService, useClass: VoidInfineaService },
    { provide: IngenicoService, useClass: VoidIngenicoService },
    ENV_PROVIDERS,
    APP_PROVIDERS,
    { provide: 'Window', useValue: window },
    NgRadio,
    GlobalErrorService,
    PdlWrapperService,
    PermissionOverrideModalService,
    UIPermissionService,
    ReceiptRetrievalFeatureRouteGuard,
    { provide: RepdashStoreUtilsService, useClass: VoidRepdashStoreUtilsService },
  ],
  bootstrap: [WebComponent],
})
export class WebModule extends RootModule {
  constructor(
    appRef: ApplicationRef,
    appState: AppStateService,
    workerIpc: WorkerIpcService,
    customLoader: AppCustomPreloader
  ) {
    super(appRef, appState, workerIpc, customLoader);
  }

  public onLoad(): void {
    console.log(`Web Application loaded: ${new Date()}`);
  }
}
